// src/components/Signup.js
//
// Tenant onboarding wizard.
// ---------------------------------------------------------------------
// A five-step flow that turns a cold signup into a school ready to run
// its first halaqa:
//   0. Admin account       — email / password / display name
//   1. Madrasa info        — tenant name + location
//   2. Teacher invites     — paste up to N emails, creates placeholder users
//   3. First class         — name + level + weekly quota
//   4. Welcome + CTA       — "Start today's class" → /daily-progress
//
// Steps 0-1 are collected before any writes happen. When the admin hits
// "Create madrasa" on step 1 we run the auth/tenant creation; that gives
// us a tenantId for the remaining steps. Steps 2-3 are skippable — a
// solo مُحفِّظ signing up at home can fly straight to the dashboard.
// ---------------------------------------------------------------------

import React, { useMemo, useState } from 'react';
import BayanIcon from './BayanIcon';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Link as MuiLink,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Autocomplete,
} from '@mui/material';
import { Email as EmailIcon, FlashOn } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { tenantService } from '../services/tenantService';
import { ROLES } from '../services/userService';
import { inviteService, parseEmailList } from '../services/inviteService';
import { classService } from '../services/firebaseService';
import { normaliseReferralCode } from '../services/referralService';
import { MaghribiHeading, ZelligeBackdrop } from './sacred';
import ArabicTextField from './ArabicTextField';
import MOROCCO_CITIES from '../data/moroccoCities';

const MIN_PASSWORD = 6;
const MAX_INVITES_PER_STEP = 10;

const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, refreshProfile, currentUser } = useAuth();
  const [searchParams] = useSearchParams();

  // Phase 5 §K.2 — pick up ?ref=CODE if a signup link came from an
  // existing tenant's referral share. Normalised immediately so every
  // downstream consumer sees the canonical uppercase form.
  const referredByCode = useMemo(
    () => normaliseReferralCode(searchParams.get('ref') || ''),
    [searchParams],
  );

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState({
    adminName: '',
    email: '',
    password: '',
    passwordConfirm: '',
    tenantName: '',
    location: '',
    inviteEmails: '',
    firstClassName: '',
    firstClassLevel: '',
    firstClassWeeklyQuota: '',
  });
  const [tenantId, setTenantId] = useState(null);
  const [inviteSummary, setInviteSummary] = useState(null);
  const [firstClassId, setFirstClassId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const steps = [
    t('signup.stepAccount'),
    t('signup.stepMadrasa'),
    t('signup.stepInvites'),
    t('signup.stepFirstClass'),
    t('signup.stepWelcome'),
  ];

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const validateStep = () => {
    if (activeStep === 0) {
      if (!form.email || !form.password) {
        setError(t('signup.errorMissingCredentials'));
        return false;
      }
      if (form.password.length < MIN_PASSWORD) {
        setError(t('signup.errorPasswordTooShort', { min: MIN_PASSWORD }));
        return false;
      }
      if (form.password !== form.passwordConfirm) {
        setError(t('signup.errorPasswordMismatch'));
        return false;
      }
    }
    if (activeStep === 1 && !form.tenantName) {
      setError(t('signup.errorMissingTenantName'));
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((s) => Math.max(0, s - 1));
  };

  /**
   * Step-2 submit: creates the Firebase Auth user + tenant doc and moves
   * the wizard into step 3 (invites). Once this runs we have a tenantId
   * and cannot go back to change the email or tenant name.
   */
  const handleCreateMadrasa = async () => {
    if (!validateStep()) return;
    setLoading(true);
    setError('');
    try {
      const cred = await register({
        email: form.email,
        password: form.password,
        displayName: form.adminName,
        role: ROLES.ADMIN,
      });
      const tid = await tenantService.createTenantWithOwner({
        ownerUid: cred.user.uid,
        tenantName: form.tenantName,
        location: form.location,
        displayName: form.adminName,
        email: form.email,
        referredByCode: referredByCode || null,
      });
      // `createTenantWithOwner` historically returns the tenantId (string)
      // or the created tenant object. Accept both shapes.
      const resolvedTid =
        typeof tid === 'string' ? tid : tid?.id || tid?.tenantId || null;
      setTenantId(resolvedTid);
      if (typeof refreshProfile === 'function') {
        await refreshProfile();
      }
      setActiveStep(2);
    } catch (err) {
      console.error('Signup failed:', err);
      setError(err?.message || t('signup.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvites = async () => {
    const { valid, invalid } = parseEmailList(form.inviteEmails);
    if (valid.length === 0 && invalid.length === 0) {
      // Admin left the box empty — skip this step.
      setActiveStep(3);
      return;
    }
    if (valid.length > MAX_INVITES_PER_STEP) {
      setError(t('signup.errorTooManyInvites', { max: MAX_INVITES_PER_STEP }));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await inviteService.inviteMany({
        tenantId,
        emails: valid,
        role: ROLES.TEACHER,
        invitedBy: currentUser?.uid || null,
      });
      setInviteSummary({ ...result, invalid });
      setActiveStep(3);
    } catch (err) {
      setError(err?.message || t('signup.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFirstClass = async () => {
    const name = form.firstClassName.trim();
    if (!name) {
      // Skipping the first class is allowed.
      setActiveStep(4);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const id = await classService.createClass({
        name,
        level: form.firstClassLevel.trim() || null,
        weeklyQuota: Number(form.firstClassWeeklyQuota) || null,
        tenantId,
        createdBy: currentUser?.uid || null,
      });
      setFirstClassId(id);
      setActiveStep(4);
    } catch (err) {
      setError(err?.message || t('signup.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const primaryButton = () => {
    if (activeStep === 1) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateMadrasa}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            t('signup.createMadrasa')
          )}
        </Button>
      );
    }
    if (activeStep === 2) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={handleSendInvites}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={22} color="inherit" />
          ) : form.inviteEmails.trim() === '' ? (
            t('signup.skip')
          ) : (
            t('signup.sendInvites')
          )}
        </Button>
      );
    }
    if (activeStep === 3) {
      return (
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateFirstClass}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={22} color="inherit" />
          ) : form.firstClassName.trim() === '' ? (
            t('signup.skip')
          ) : (
            t('signup.createClass')
          )}
        </Button>
      );
    }
    if (activeStep === 4) {
      return null; // welcome step has its own CTAs below
    }
    return (
      <Button variant="contained" onClick={handleNext} disabled={loading}>
        {t('signup.next')}
      </Button>
    );
  };

  return (
    <Box
      sx={(th) => ({
        position: 'relative',
        minHeight: '100dvh',
        maxHeight: '100dvh',
        overflowY: 'auto',
        bgcolor: th.palette.sacred.surface,
        py: 'clamp(0.75rem, 2vh, 2rem)',
        px: 2,
      })}
    >
      <ZelligeBackdrop />
      <Container component="main" maxWidth="sm" sx={{ position: 'relative' }}>
        <Card sx={{ bgcolor: 'sacred.surfaceContainerLow' }}>
          <CardContent sx={{ p: 'clamp(1rem, 2.5vh, 2rem) !important' }}>
            <Box sx={{ textAlign: 'center', mb: 'clamp(0.75rem, 2vh, 1.5rem)' }}>
              <MaghribiHeading variant="headline" glow sx={{ fontSize: 'clamp(1.6rem, 4vw, 2.2rem)', lineHeight: 1.3, mb: 0.5 }}>
                {t('app.name')}
              </MaghribiHeading>
              <Typography sx={{ fontFamily: 'var(--sacred-font-body)', color: 'sacred.onSurfaceVariant', fontSize: 'clamp(0.85rem, 1.6vw, 1rem)' }}>
                {t('signup.title')}
              </Typography>
            </Box>

            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 'clamp(0.75rem, 2vh, 1.5rem)' }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {error && <Alert severity="error" sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)' }}>{error}</Alert>}

            {referredByCode && activeStep < 2 && (
              <Alert severity="info" sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)' }} icon={false}>
                {t('signup.referredBanner', { code: referredByCode })}
              </Alert>
            )}

            {activeStep === 0 && (
              <Stack spacing={'clamp(0.5rem, 1.5vh, 1rem)'}>
                <ArabicTextField
                  label={t('signup.adminName')}
                  fullWidth
                  size="small"
                  value={form.adminName}
                  onChange={handleChange('adminName')}
                />
                <TextField
                  label={t('auth.email')}
                  type="email"
                  fullWidth
                  required
                  size="small"
                  value={form.email}
                  onChange={handleChange('email')}
                />
                <TextField
                  label={t('auth.password')}
                  type="password"
                  fullWidth
                  required
                  size="small"
                  value={form.password}
                  onChange={handleChange('password')}
                  helperText={t('signup.passwordHelp', { min: MIN_PASSWORD })}
                />
                <TextField
                  label={t('signup.passwordConfirm')}
                  type="password"
                  fullWidth
                  required
                  size="small"
                  value={form.passwordConfirm}
                  onChange={handleChange('passwordConfirm')}
                />
              </Stack>
            )}

            {activeStep === 1 && (
              <Stack spacing={'clamp(0.5rem, 1.5vh, 1rem)'}>
                <ArabicTextField
                  label={t('signup.tenantName')}
                  fullWidth
                  required
                  size="small"
                  value={form.tenantName}
                  onChange={handleChange('tenantName')}
                />
                {/* City picker — stored on tenant.location and printed on
                    the certificate date line. freeSolo allows any city not
                    in the preset Moroccan list. */}
                <Autocomplete
                  freeSolo
                  autoHighlight
                  options={MOROCCO_CITIES}
                  value={form.location || ''}
                  onChange={(_e, val) => handleChange('location')({ target: { value: val || '' } })}
                  onInputChange={(_e, val) => handleChange('location')({ target: { value: val || '' } })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('signup.location')}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Stack>
            )}

            {activeStep === 2 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)' }}>
                  {t('signup.invitesHelp', { max: MAX_INVITES_PER_STEP })}
                </Typography>
                <TextField
                  label={t('signup.invitesLabel')}
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  size="small"
                  placeholder={'teacher1@example.com\nteacher2@example.com'}
                  value={form.inviteEmails}
                  onChange={handleChange('inviteEmails')}
                  helperText={t('signup.invitesHelperText')}
                />
              </Box>
            )}

            {activeStep === 3 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 'clamp(0.5rem, 1.5vh, 1rem)' }}>
                  {t('signup.firstClassHelp')}
                </Typography>
                <Stack spacing={'clamp(0.5rem, 1.5vh, 1rem)'}>
                  <ArabicTextField
                    label={t('signup.firstClassName')}
                    fullWidth
                    size="small"
                    value={form.firstClassName}
                    onChange={handleChange('firstClassName')}
                    placeholder={t('signup.firstClassNamePlaceholder')}
                  />
                  <ArabicTextField
                    label={t('signup.firstClassLevel')}
                    fullWidth
                    size="small"
                    value={form.firstClassLevel}
                    onChange={handleChange('firstClassLevel')}
                  />
                  <TextField
                    label={t('signup.firstClassWeeklyQuota')}
                    type="number"
                    fullWidth
                    size="small"
                    value={form.firstClassWeeklyQuota}
                    onChange={handleChange('firstClassWeeklyQuota')}
                    inputProps={{ min: 0, max: 60 }}
                    helperText={t('signup.firstClassWeeklyQuotaHelp')}
                  />
                </Stack>
              </Box>
            )}

            {activeStep === 4 && (
              <Box sx={{ textAlign: 'center', py: 'clamp(0.5rem, 1.5vh, 1rem)' }}>
                <BayanIcon name="big_check" sx={{ fontSize: 'clamp(40px, 7vh, 72px)', color: 'success.main', mb: 1 }} />
                <MaghribiHeading variant="title" sx={{ fontSize: 'clamp(1.2rem, 3vw, 1.6rem)', mb: 1 }}>
                  {t('signup.welcomeHeading', { name: form.tenantName })}
                </MaghribiHeading>
                <Typography sx={{ color: 'text.secondary', mb: 'clamp(0.75rem, 2vh, 1.5rem)' }}>
                  {t('signup.welcomeSubtitle')}
                </Typography>

                {(inviteSummary || firstClassId) && (
                  <List dense sx={{ textAlign: 'start', mb: 2 }}>
                    {inviteSummary && (
                      <>
                        {inviteSummary.created.length > 0 && (
                          <ListItem>
                            <EmailIcon color="success" sx={{ mr: 1 }} />
                            <ListItemText
                              primary={t('signup.summaryInvitesCreated', {
                                count: inviteSummary.created.length,
                              })}
                            />
                          </ListItem>
                        )}
                        {inviteSummary.duplicates.length > 0 && (
                          <ListItem>
                            <EmailIcon color="disabled" sx={{ mr: 1 }} />
                            <ListItemText
                              primary={t('signup.summaryInvitesDuplicate', {
                                count: inviteSummary.duplicates.length,
                              })}
                            />
                          </ListItem>
                        )}
                        {(inviteSummary.failed.length > 0 ||
                          inviteSummary.invalid.length > 0) && (
                          <ListItem>
                            <Chip
                              size="small"
                              color="warning"
                              label={t('signup.summaryInvitesRejected', {
                                count:
                                  inviteSummary.failed.length +
                                  inviteSummary.invalid.length,
                              })}
                            />
                          </ListItem>
                        )}
                      </>
                    )}
                    {firstClassId && (
                      <ListItem>
                        <BayanIcon name="student" color="success" sx={{ mr: 1 }} />
                        <ListItemText
                          primary={t('signup.summaryClassCreated', {
                            name: form.firstClassName,
                          })}
                        />
                      </ListItem>
                    )}
                  </List>
                )}

                <Stack direction="column" spacing={1}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<FlashOn />}
                    onClick={() => navigate('/daily-progress')}
                    sx={{ fontFamily: 'var(--sacred-font-display)' }}
                  >
                    {t('signup.startClassCta')}
                  </Button>
                  <Button onClick={() => navigate('/admin')}>
                    {t('signup.openAdminCta')}
                  </Button>
                </Stack>
              </Box>
            )}

            {activeStep !== 4 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 'clamp(0.75rem, 2vh, 1.5rem)' }}>
                <Button
                  onClick={handleBack}
                  disabled={activeStep === 0 || activeStep >= 2 || loading}
                >
                  {t('signup.back')}
                </Button>
                {primaryButton()}
              </Box>
            )}

            <Box sx={{ textAlign: 'center', mt: 'clamp(0.75rem, 2vh, 1.5rem)' }}>
              <MuiLink
                component={RouterLink}
                to="/login"
                sx={{
                  color: 'sacred.primary',
                  fontWeight: 600,
                  fontFamily: 'var(--sacred-font-body)',
                }}
              >
                {t('signup.alreadyHaveAccount')}
              </MuiLink>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Signup;
